import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Clock, Check, X, Loader2, AlertCircle, CheckCircle2,
  MapPin, User as UserIcon,
} from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import { API, fmtDayLong } from './_helpers';

export default function SwapRespondPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const initial = params.get('action');
  const [sw, setSw] = useState(null);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/roster/swap-respond/${token}`)
      .then(r => {
        setSw(r.data);
        // Honour server-truth: if swap already resolved, show that state immediately
        if (r.data.status && r.data.status !== 'pending') setDone(r.data.status);
      })
      .catch(e => setErr(e?.response?.data?.detail || 'Swap link no longer valid'));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try { await axios.post(`${API}/roster/swap-respond/${token}/accept`); setDone('accepted'); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not accept'); }
    setBusy(false);
  };
  const decline = async () => {
    setBusy(true);
    try { await axios.post(`${API}/roster/swap-respond/${token}/decline`, { reason }); setDone('declined'); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not decline'); }
    setBusy(false);
  };

  const fmt = (snap) => {
    if (!snap) return null;
    return (
      <div className="rounded-lg border bg-muted/30 p-3 space-y-1 text-sm">
        <div className="font-semibold" style={{ color: '#0d2644' }}>{fmtDayLong(snap.date)}</div>
        <div><Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{snap.start} – {snap.end}</div>
        {snap.role_label && <div><UserIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{snap.role_label}</div>}
        {snap.location && <div><MapPin className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{snap.location}</div>}
      </div>
    );
  };

  const kindWord = sw?.kind === 'swap' ? 'swap' : 'give-away';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl border shadow-sm p-6">
        <div className="text-xs font-bold tracking-[0.18em] uppercase mb-2" style={{ color: '#7c3aed' }}>i3 RADIOLOGY · SHIFT {kindWord.toUpperCase()}</div>
        {err && (
          <div className="text-center">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
            <p className="font-semibold">{err}</p>
            <Link to="/" className="mt-3 inline-block text-sm underline">Go to dashboard</Link>
          </div>
        )}
        {!err && !sw && <Loader2 className="w-6 h-6 animate-spin mx-auto" />}
        {!err && sw && !done && (
          <>
            <h2 className="text-xl font-bold mb-1" style={{ color: '#0d2644' }}>
              {sw.requester_name} wants you to take a shift
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Hi {sw.target_name}, please {sw.kind === 'swap' ? 'review the proposed swap below' : 'accept or decline this shift'}.
            </p>

            <div className="text-[10px] font-bold uppercase tracking-wider text-green-700 mb-1">Shift on offer</div>
            {fmt(sw.requester_shift_snapshot)}

            {sw.kind === 'swap' && sw.target_shift_snapshot && (
              <>
                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1 mt-3">In exchange for your shift</div>
                {fmt(sw.target_shift_snapshot)}
              </>
            )}

            {sw.message && (
              <div className="text-sm italic text-muted-foreground border-l-2 border-slate-300 pl-3 mt-3">
                &ldquo;{sw.message}&rdquo;
              </div>
            )}

            {initial !== 'decline' && (
              <button onClick={accept} disabled={busy} data-testid="swap-respond-accept"
                className="mt-4 w-full text-sm font-bold py-3 rounded-lg bg-green-600 text-white inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <Check className="w-5 h-5" /> Accept {kindWord}
              </button>
            )}
            {initial !== 'accept' && (
              <>
                <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for declining (optional)" rows={2} className="mt-3 text-sm" />
                <button onClick={decline} disabled={busy} data-testid="swap-respond-decline"
                  className="mt-2 w-full text-sm font-bold py-3 rounded-lg border-2 border-red-500 text-red-600 inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  <X className="w-5 h-5" /> Decline
                </button>
              </>
            )}
          </>
        )}
        {done === 'accepted' && (
          <div className="text-center py-6">
            <CheckCircle2 className="w-14 h-14 mx-auto mb-3 text-green-600" />
            <h2 className="text-xl font-bold">Swap accepted</h2>
            <p className="text-sm text-muted-foreground mt-1">The shift is now yours. The practice manager has been notified.</p>
          </div>
        )}
        {done === 'declined' && (
          <div className="text-center py-6">
            <X className="w-14 h-14 mx-auto mb-3 text-red-500" />
            <h2 className="text-xl font-bold">Declined</h2>
            <p className="text-sm text-muted-foreground mt-1">{sw?.requester_name} has been notified. No worries.</p>
          </div>
        )}
        {done === 'cancelled' && (
          <div className="text-center py-6">
            <AlertCircle className="w-14 h-14 mx-auto mb-3 text-slate-500" />
            <h2 className="text-xl font-bold">Cancelled</h2>
            <p className="text-sm text-muted-foreground mt-1">{sw?.requester_name} cancelled this swap before you responded.</p>
          </div>
        )}
      </div>
    </div>
  );
}
